type ContentType = 'event' | 'article' | 'video' | 'ranking' | 'result'

interface ListAnalytics {
  /**
   * The title of the content item.
   *
   * @example
   * article.title // "How to Watch..."
   */
  name: string

  /** The index of the item in the list. */
  item_order: number

  /** The length of the list in which the item appeared */
  array_length: number

  /**
   * The ID of the content item.
   *
   * @example
   * article.id
   */
  node_id: number

  /**
   * The discriminator for the type of the node.
   *
   * @example
   * article.type // 'article'
   */
  node_type: ContentType
}

/**
 * This is a special set of analytics data for events. If you have an event in
 * a list, you should create this type.
 */
export interface ListItemEventAnalytics extends ListAnalytics {
  /** The ID of the live event attached to the event. */
  live_id?: number

  /** The status of the live event attached to the event. */
  event_status?: string

  /** Events must always have type 'event' */
  node_type: 'event'
}

/**
 * This interface describes the analytics that must be provided for a content
 * item in a list when the user clicks it as a link.
 *
 * @remarks
 * If your content type is `Event` then you should be creating the
 * EventListItemAnalytics type.
 *
 * @see ListItemEventAnalytics
 */
export interface ListItemAnalytics extends ListAnalytics {
  /** If the content is marked as premium. False by default. */
  is_premium: boolean

  /**
   * The ID of the primary association of the content item.
   *
   * @example
   * article.drill_through_link.id
   */
  primary_association_id?: number

  /**
   * The title of the primary association of the content item.
   *
   * @example
   * article.drill_through_link.title
   */
  primary_association_title?: string

  node_type: Exclude<ContentType, 'event'>
}

interface Content<Type extends ContentType> {
  type: Type
  title: string
  id: number
}

export interface EventContent extends Content<'event'> {
  type: 'event'
  live_event: {
    id: number
    status: string
  } | null
}

export interface NonEventContent extends Content<Exclude<ContentType, 'event'>> {
  premium: boolean
  drill_through_link?: {
    id: number
    name: string
  } | null
}

// prettier-ignore
export function getEventAnalytics(
  contentItem: EventContent,
  index: number,
  listLength: number
): ListItemEventAnalytics {
  return {
    name: contentItem.title,
    item_order: index,
    array_length: listLength,
    node_id: contentItem.id,
    live_id: contentItem.live_event?.id,
    event_status: contentItem.live_event?.status,
    node_type: contentItem.type
  }
}

/**
 * Not for use with Event.
 *
 * @see getEventAnalytics
 */
// prettier-ignore
export function getContentAnalytics(
  contentItem: NonEventContent,
  index: number,
  listLength: number
): ListItemAnalytics {
  return {
    name: contentItem.title,
    node_id: contentItem.id,
    primary_association_id: contentItem.drill_through_link?.id,
    primary_association_title: contentItem.drill_through_link?.name,
    is_premium: contentItem.premium,
    item_order: index,
    array_length: listLength,
    node_type: contentItem.type
  }
}

// prettier-ignore
export function getListItemAnalytics(
  contentItem: EventContent | NonEventContent,
  index: number,
  listLength: number
): ListItemAnalytics | ListItemEventAnalytics {
  switch (contentItem.type) {
    case 'event':
      return getEventAnalytics(contentItem, index, listLength)
    default:
      return getContentAnalytics(contentItem, index, listLength)
  }
}

export interface ContentWithAnalytics<A> {
  contentItem: A
  analytics: ListItemAnalytics | ListItemEventAnalytics
}

export function createContentListWithAnalytics<A extends EventContent | NonEventContent>(
  contentItems: ReadonlyArray<A>
): ReadonlyArray<ContentWithAnalytics<A>> {
  const makeContentWithAnalyticsList = (a: A, index: number): ContentWithAnalytics<A> => {
    return {
      contentItem: a,
      analytics: getListItemAnalytics(a, index, contentItems.length)
    }
  }
  return contentItems.map(makeContentWithAnalyticsList)
}

export function isEventAnalytics(e: ListItemEventAnalytics | ListItemAnalytics): e is ListItemEventAnalytics {
  return e.node_type === 'event'
}
